import React from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

/**
 * US Destinations information page
 * Shows customs and shipping information for US destinations
 */
function DestinationsUS() {
  const [searchParams] = useSearchParams();
  const minBags = searchParams.get('min') || '1';
  const fromCountry = searchParams.get('from') || 'US';

  return (
    <div style={{ backgroundColor: '#17a2b8', minHeight: '400px', position: 'relative' }}>
      {/* Hero Section */}
      <Container className="py-5">
        <Row>
          <Col lg={6} className="text-white">
            <h1 className="display-4 fw-bold mb-3">Send to United States from $55</h1>
            <p className="lead mb-4">Get a quote to ship to United States</p>
            
            {/* Quote Form */}
            <Card className="shadow-sm border-0 mb-4" style={{ maxWidth: '400px' }}>
              <Card.Body className="p-3">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">From</Form.Label>
                    <Form.Select>
                      <option>United States</option>
                      <option>United Kingdom</option>
                      <option>Canada</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">To</Form.Label>
                    <Form.Select>
                      <option>United States</option>
                      <option>United Kingdom</option>
                      <option>Canada</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Button variant="success" className="w-100 fw-bold">
                    Show Prices
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={6} className="text-white text-center">
            <div className="mb-4">
              <h3 className="mb-3">"Celebrating 20 years & millions of moves"</h3>
              <a href="#" className="text-white text-decoration-underline">Join the party</a>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <div className="text-warning">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star-half-alt"></i>
              </div>
              <span>Trustpilot</span>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Content Sections */}
      <div className="bg-light">
        <Container className="py-5">
          <Row className="g-4 mb-5">
            {/* Customs Information */}
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-5">
                  <div className="mb-4">
                    <i className="fas fa-passport fa-4x text-primary"></i>
                  </div>
                  <h3 className="mb-3">Customs Information</h3>
                  <p className="text-muted mb-4">
                    Please take a moment to review the important customs information before shipping to USA.
                  </p>
                  <a href="#customs-details" className="btn btn-outline-primary">Show More</a>
                </Card.Body>
              </Card>
            </Col>

            {/* Prohibited Items */}
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-5">
                  <div className="mb-4">
                    <i className="fas fa-ban fa-4x text-danger"></i>
                  </div>
                  <h3 className="mb-3">Prohibited Items</h3>
                  <p className="text-muted mb-4">
                    With Send My Bag™ you can travel with almost everything you would normally find in luggage, for security and customs reasons however certain items are prohibited, please find these listed below.
                  </p>
                  <a href="#prohibited-details" className="btn btn-outline-primary">Show More</a>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Customs Information Details */}
          <section id="customs-details" className="mb-5">
            <Card className="shadow-sm border-0">
              <Card.Body className="p-5">
                <h2 className="mb-4">Customs Information</h2>
                <p className="mb-4">
                  Please take a moment to review the important customs information before shipping to USA.
                </p>

                <h4 className="mt-4 mb-3">Personal Effects Customs Clearance USA</h4>
                <p>
                  When shipping personal effects to the United States, all items must be declared accurately on customs forms. 
                  The US Customs and Border Protection (CBP) requires detailed information about the contents of your shipment.
                </p>

                <h5 className="mt-4 mb-3">Temporary Import and Returning US Residents</h5>
                <p>
                  US residents returning home with personal effects that were taken abroad are generally eligible for duty-free entry, 
                  provided the items can be identified as having been previously exported from the US. Keep receipts and documentation 
                  to prove ownership and prior export.
                </p>

                <h5 className="mt-4 mb-3">Moving to the USA</h5>
                <p>
                  If you are moving to the United States permanently, you may be eligible for duty-free entry of household goods and 
                  personal effects under certain conditions. You must have owned and used these items abroad for at least one year, 
                  and you must import them within a reasonable time after your arrival in the US.
                </p>

                <h5 className="mt-4 mb-3">General Personal Effects Customs Information</h5>
                <p>
                  Personal effects are typically defined as used items which have been in your possession for at least 6 months. 
                  If your shipment is checked by customs and found to contain brand new or commercial items, additional duties and 
                  taxes may apply. Always declare items accurately to avoid delays or penalties.
                </p>

                <h5 className="mt-4 mb-3">Customs Clearance Time</h5>
                <p>
                  Most shipments to the USA are processed quickly by customs. However, if your shipment is selected for inspection, 
                  it may be held for 1-3 business days while customs officers review the contents and documentation.
                </p>

                <h5 className="mt-4 mb-3">Shipping Gifts to the USA</h5>
                <p>
                  Gifts valued at $100 or less can be sent duty-free to the United States, provided they are sent to someone other 
                  than the shipper and are properly marked as gifts. Gifts exceeding this value may be subject to duties and taxes.
                </p>
              </Card.Body>
            </Card>
          </section>

          {/* Prohibited Items Details */}
          <section id="prohibited-details" className="mb-5">
            <Card className="shadow-sm border-0">
              <Card.Body className="p-5">
                <h2 className="mb-4">Prohibited Items</h2>
                
                <Alert variant="danger" className="mb-4">
                  <strong>Important:</strong> The following items are strictly prohibited and cannot be shipped to the United States.
                </Alert>

                <Row>
                  <Col md={6}>
                    <h5 className="text-danger mb-3">Absolutely Prohibited:</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Illegal drugs and substances</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Weapons and ammunition</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Explosives and fireworks</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Hazardous materials</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Counterfeit goods</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Endangered species products</li>
                      <li className="mb-2"><i className="fas fa-times-circle text-danger me-2"></i>Obscene materials</li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <h5 className="text-warning mb-3">Restricted Items:</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Lithium batteries (must be installed in devices)</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Aerosols and pressurized containers</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Food products (restrictions apply)</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Plant and animal products</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Alcohol and tobacco (duty limits apply)</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Prescription medications (documentation required)</li>
                      <li className="mb-2"><i className="fas fa-exclamation-triangle text-warning me-2"></i>Plastic bags and plastic boxes</li>
                    </ul>
                  </Col>
                </Row>

                <Alert variant="info" className="mt-4">
                  <strong>Note:</strong> This is not an exhaustive list. For complete information about prohibited and restricted items, 
                  please consult the US Customs and Border Protection website or contact our customer service team.
                </Alert>
              </Card.Body>
            </Card>
          </section>

          {/* US Route Alerts */}
          <section className="mb-4">
            <Alert variant="warning" className="border-warning">
              <h4 className="alert-heading mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                United States Route Alerts
              </h4>
              <p className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Bookings need to be placed prior to 20:00ET for collection requests for the following day.
              </p>
            </Alert>
          </section>
        </Container>
      </div>
    </div>
  );
}

export default DestinationsUS;
